/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';
import * as angular from 'angular';
import * as moment from 'moment';
import {
	AnalyticsEventsService,
	analyticsEventsService,
} from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import DateValidationConstants from 'src/app/app-data/validators/date-validators.json';

import { ProjectNotifier } from 'src/app/services/project-notifier.service';
import { Rectangle } from 'pixi.js-legacy';
import { overlapRect } from 'utils/ticket-utils';
import { DateCache } from '@touchplan/common';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import firebase from 'firebase/compat/app';

export const YEAR_RANGE = '1950:2100';

export class DatePositionValidator {
	public static $inject = ['projectNotifier', 'fbConnection', 'popup'];
	private planData: any = {};
	private projData: any = {};
	private _lastProjectId: string;
	private _lastPlanId: string;
	public analyticsEventsService: AnalyticsEventsService;
	private logger = LoggerServiceFactory.create('DatePositionValidator');
	constructor(private projectNotifier: ProjectNotifier, private fbConnection, private popup) {
		// Use this to update reference paths for the plan data.
		projectNotifier.projectState.subscribe((state) => {
			this.destroy();
			if (state && state.projectId) {
				this.setup(state.projectId, state.planId);
			}
		});

		this.analyticsEventsService = analyticsEventsService;
	}

	public static getYearRange(
		numYearsBack: number = DateValidationConstants.numYearsBack,
		numYearsForward: number = DateValidationConstants.numYearsForward
	): string {
		const minYearRange: string = moment().subtract(numYearsBack, 'years').format(DateCache.defaultFormat);
		const maxYearRange: string = moment().add(numYearsForward, 'years').format(DateCache.defaultFormat);
		return `${minYearRange}:${maxYearRange}`;
	}

	// Get the plan data. fbdb will write plan context information here.
	private setup(projectId, planId) {
		if (planId) {
			const plansPath = fancyFirebaseGetters.getPlanPath(projectId, planId);
			this.planData = this.fbConnection.fbObject(
				fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(plansPath, projectId)
			);
		}
		const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
		this.projData = this.fbConnection.fbObject(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
		);
	}
	private destroy() {
		if (this.planData && this.planData.$destroy) {
			this.planData.$destroy();
			this.planData = null;
		}
		if (this.projData && this.projData.$destroy) {
			this.projData.$destroy();
			this.projData = null;
		}
	}

	private isSameState(projectId: string, planId?: string) {
		return projectId === this._lastProjectId && planId === this._lastPlanId;
	}

	public forceState(projectId: string, planId?: string) {
		if (this.isSameState(projectId, planId)) {
			return;
		}
		this.destroy();
		this.setup(projectId, planId);
	}

	/**
	 * DATE VALIDATOR API STARTS YONDER
	 */
	public isValidDate(compareDate, srcData: any = {}, units?, inclusive?) {
		// MOC-3083. Null/undefined compareDate is assumed to be a valid move.
		if (!compareDate || !DateValidationConstants.enableValidator || !srcData) {
			return true;
		}
		if (!moment(compareDate, 'YYYY-MM-DD', true).isValid()) {
			return false;
		}
		const startDate = srcData.minDate || moment();
		const endDate = srcData.maxDate || moment();
		const min = moment(startDate).subtract(DateValidationConstants.daysWarningOffset, 'days');
		const max = moment(endDate).add(DateValidationConstants.daysWarningOffset, 'days');
		units = units || 'days';
		inclusive = angular.isDefined(inclusive) ? inclusive : true;
		return moment(compareDate).isBetween(min, max, units, inclusive);
	}

	public isBetween(num, min, max) {
		if (num === Infinity) {
			return true;
		}
		return num >= min && num <= max;
	}

	/**
	 * Check if the position is within the "valid" range
	 * See apps/frontend/src/app/app-data/validators/date-validators.json for the configuration
	 * xWarningOffset and xWarningThreshHold are in pixels (145 pixels = 1 day)
	 */
	public isValidPosition(x, y, srcData?) {
		if (!srcData) {
			srcData = this.planData;
			if (!srcData) {
				return true;
			}
		}
		if (!DateValidationConstants.enableValidator || !srcData.minX || !srcData.maxX) {
			return true;
		}
		const minX = srcData.minX - DateValidationConstants.xWarningOffset;
		const maxX = srcData.maxX + DateValidationConstants.xWarningOffset;
		const minY = srcData.minY - DateValidationConstants.yWarningOffset;
		const maxY = srcData.maxY + DateValidationConstants.yWarningOffset;
		return this.isBetween(x, minX, maxX) && this.isBetween(y, minY, maxY);
	}

	public isSwimlanePositionValid(globalY, globalHeight, srcData?) {
		if (!srcData) {
			srcData = this.planData;
			// this.planData is null for new plans (No tickets yet) or if this method runs prior to fbdb calculating the bounds.
			// TP-538: If srcData exists, but fbdb did not write plan dimensions, assume new plan and allow the write
			if (!srcData || srcData.minX === undefined || srcData.minY === undefined) {
				return true;
			}
		}
		if (!DateValidationConstants.enableValidator) {
			return true;
		}
		const minY = srcData.minY - DateValidationConstants.yWarningOffset;
		const maxY = srcData.maxY + DateValidationConstants.yWarningOffset;
		const globalViewRect = new Rectangle(Infinity, globalY, Infinity, globalHeight);
		const planBounds = new Rectangle(Infinity, minY, Infinity, maxY - minY);
		return overlapRect(globalViewRect, planBounds);
	}

	public isExtremelyInvalidPosition(x, y) {
		return (
			Math.abs(x) > DateValidationConstants.abyssLimit || Math.abs(y) > DateValidationConstants.abyssLimit
		);
	}

	public dateWarning(compareDate, srcData, units?, inclusive?) {
		// MOC-3083
		if (!compareDate) {
			return false;
		}
		if (!DateValidationConstants.enableValidator || !srcData) {
			return true;
		}
		const threshold = DateValidationConstants.daysWarning;
		units = units || 'days';
		const startDate = srcData.minDate || moment();
		const endDate = srcData.maxDate || moment();
		const min = moment(startDate).subtract(threshold, 'days');
		const max = moment(endDate).add(threshold, 'days');
		inclusive = angular.isDefined(inclusive) ? inclusive : true;
		return moment(compareDate).isBetween(min, max, units, inclusive);
	}

	/**
	 * Check if the position is within the "valid" range
	 * See apps/frontend/src/app/app-data/validators/date-validators.json for the configuration
	 * xWarningThreshHold and xWarningThreshHold are in pixels (145 pixels = 1 day)
	 */
	public positionWarning(x: number, y: number, dataSrc?) {
		if (!dataSrc) {
			dataSrc = this.planData;
		}
		if (
			!DateValidationConstants.enableValidator ||
			!dataSrc ||
			!dataSrc.minX ||
			!dataSrc.maxX ||
			!dataSrc.minY ||
			!dataSrc.maxY
		) {
			return true;
		}
		const minX = dataSrc.minX - DateValidationConstants.xWarningThreshHold;
		const maxX = dataSrc.maxX + DateValidationConstants.xWarningThreshHold;
		const minY = dataSrc.minY - DateValidationConstants.yWarningThreshHold;
		const maxY = dataSrc.maxY + DateValidationConstants.yWarningThreshHold;
		return this.isBetween(x, minX, maxX) && this.isBetween(y, minY, maxY);
	}

	public verifyDate(compareDate, settingsObj, confirmCb, cancelCb?) {
		const args = [].slice.call(arguments, 4);
		if (!DateValidationConstants) {
			return confirmCb.apply(undefined, arguments);
		}
		cancelCb = cancelCb || function () {};
		if (this.dateWarning(compareDate, this.planData) && !this.isValidDate(compareDate, this.planData)) {
			this.analyticsEventsService.dateViolationNagConfirm();
			return this.popup(settingsObj)
				.then(() => {
					confirmCb.apply(undefined, args);
				})
				.catch((err) => {
					this.logger.warn('verifyDate', err);
					cancelCb.apply(undefined, args);
				});
		} else if (
			!this.dateWarning(compareDate, this.planData) &&
			!this.isValidDate(compareDate, this.planData)
		) {
			Logging.warning(
				'Your defined date is too far away from the current plan. Please choose a different date.'
			);
			this.analyticsEventsService.dateViolationBlocked();
			return cancelCb.apply(undefined, args);
		} else if (this.isValidDate(compareDate, this.planData)) {
			return confirmCb.apply(undefined, args);
		}
		return cancelCb.apply(undefined, args);
	}

	public verifyProjectDate(compareDate, settingsObj, confirmCb, cancelCb?) {
		const args = [].slice.call(arguments, 4);
		if (!DateValidationConstants) {
			return confirmCb.apply(undefined, arguments);
		}
		cancelCb = cancelCb || function () {};
		if (this.dateWarning(compareDate, this.projData) && !this.isValidDate(compareDate, this.projData)) {
			this.analyticsEventsService.dateViolationNagConfirm();
			return this.popup(settingsObj)
				.then(() => {
					confirmCb.apply(undefined, args);
				})
				.catch((err) => {
					this.logger.warn('verifyProjectDate', err);
					cancelCb.apply(undefined, args);
				});
		} else if (
			!this.dateWarning(compareDate, this.projData) &&
			!this.isValidDate(compareDate, this.projData)
		) {
			this.analyticsEventsService.dateViolationBlocked();
			Logging.warning(
				'Your defined date is too far away from the current plan. Please choose a different date.'
			);
			return cancelCb.apply(undefined, args);
		} else if (this.isValidDate(compareDate, this.projData)) {
			return confirmCb.apply(undefined, args);
		}
		return cancelCb.apply(undefined, args);
	}

	public verifyPosition(x, y, settingsObj, confirmCb, cancelCb?) {
		const args = [].slice.call(arguments, 4);
		if (!DateValidationConstants) {
			return confirmCb.apply(undefined, arguments);
		}
		cancelCb = cancelCb || function () {};
		if (this.positionWarning(x, y) && !this.isValidPosition(x, y)) {
			this.analyticsEventsService.positionViolationNagConfirm();
			return this.popup(settingsObj)
				.then(() => {
					confirmCb.apply(undefined, args);
				})
				.catch((err) => {
					this.logger.warn('verifyPosition', err);
					cancelCb.apply(undefined, args);
				});
		} else if (!this.positionWarning(x, y) && !this.isValidPosition(x, y)) {
			this.analyticsEventsService.positionViolationBlocked();
			Logging.warning('Cannot drag ticket there as it is to far away from the rest of your tickets.');
			return cancelCb.apply(undefined, args);
		} else if (this.isValidPosition(x, y)) {
			return confirmCb.apply(undefined, args);
		}
		return cancelCb.apply(undefined, args);
	}

	public askForConfirmation(settings, confirmCb, cancelCb?) {
		const args = [].slice.call(arguments, 3);
		cancelCb = cancelCb || function () {};
		return this.popup(settings)
			.then(() => {
				confirmCb.apply(undefined, args);
			})
			.catch((err) => {
				this.logger.warn('verifyPosition', err);
				cancelCb.apply(undefined, args);
			});
	}

	public getData() {
		return this.planData;
	}
}

angular.module('ticketspaceApp').service('DatePositionValidator', DatePositionValidator);
